import { TitleSpan } from '@/components/features/shared/TitleSpan'
import { DebtLabel, InlinePriceLabel, PriceLabel } from '@/components/features/shared/utils'
import { AlertWarning } from '@/components/ui/alertWarning'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { installmentsOptions } from '@/constants/installmentOptions'
import { formatAgreementStatus, getAgreementStatusColor } from '@/domain/agreements/agreement'
import { getCollectorFullName } from '@/domain/collectors/collector'
import { PaymentData, PaymentStatus } from '@/domain/payments/payments'
import { useSellers } from '@/hooks/api/useSellers'
import { CreditCard, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { InfoCard } from '../sub-components/InfoCard'

interface ConvertToPaymentPlanModalProps {
  isOpen: boolean
  onClose: () => void
  data: PaymentData | null
  isPending: boolean
  onConvert: (installments: number, installmentAmount: number, initialAmount: number) => void
}

export const ConvertToPaymentPlanModal = ({
  isOpen,
  onClose,
  data,
  isPending,
  onConvert,
}: ConvertToPaymentPlanModalProps) => {
  const { data: sellers = [] } = useSellers()
  const [installments, setInstallments] = useState<number>(3)
  const [installmentAmount, setInstallmentAmount] = useState<number>(0)
  const [initialAmount, setInitialAmount] = useState<number>(0)

  const contractAmount = data?.contractAmount ?? 0
  const amountPaid = data?.payments.filter(p => p.status === PaymentStatus.COMPLETED || p.status === PaymentStatus.IN_COLLECTOR)
    .reduce((sum, p) => sum + p.amount, 0) ?? 0
  const remainingBalance = contractAmount - amountPaid

  const installmentValue = useMemo(() => {
    return installmentAmount / installments
  }, [installmentAmount, installments])

  useEffect(() => {
    if (remainingBalance > 0) {
      setInstallmentAmount(remainingBalance)
    }
  }, [remainingBalance])

  const handleConfirm = () => {
    if (installments <= 0 || installmentAmount <= 0) return
    onConvert(installments, installmentAmount, initialAmount)
  }

  if (!data) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex flex-col items-start justify-between">
            <CardTitle className='text-xl'>Crear Plan de pago </CardTitle>
            <CardDescription>
              <h3 className="text-md font-bold flex items-center gap-2">
                {data.offeringCode} - {data.offeringName}
              </h3>
            </CardDescription>
          </div>
        </DialogHeader>

        <CardContent className="space-y-6">
          <InfoCard
            rows={[
              [
                <div key="total" className="space-y-1">
                  <TitleSpan>Monto Total</TitleSpan>
                  <PriceLabel price={contractAmount} />
                </div>,
                <div key="paid" className="space-y-1">
                  <TitleSpan>Monto Abonado</TitleSpan>
                  <PriceLabel price={amountPaid} />
                </div>,
                <div key="remaining" className="space-y-1">
                  <TitleSpan>Saldo Pendiente</TitleSpan>
                  {remainingBalance > 0 ? <DebtLabel amount={remainingBalance} /> : <PriceLabel price={0} />}
                </div>,
              ], [
                <div key="status" className="flex flex-col justify-center gap-1">
                  <TitleSpan>Estado</TitleSpan>
                  <Badge className={"w-fit " + getAgreementStatusColor(data?.status)}>
                    {formatAgreementStatus(data?.status)}
                  </Badge>
                </div>,
                <div key="seller" className="flex flex-col justify-center gap-1">
                  <TitleSpan>Vendedor</TitleSpan>
                  <span className="text-xs">{getCollectorFullName(sellers.find(s => s.id === data.sellerId))}</span>
                </div>
              ],
            ]}
          />

          <Card className="bg-muted/30">
            <CardContent className="p-4 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                {amountPaid === 0 && (
                  <div className="space-y-2">
                    <TitleSpan>Entrega inicial</TitleSpan>
                    <Input
                      type="number"
                      value={initialAmount || ''}
                      onChange={e => setInitialAmount(Number(e.target.value))}
                      disabled={isPending}
                      min={0}
                      max={contractAmount}
                    />
                  </div>
                )}
                <div />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="space-y-2">
                  <TitleSpan>Cantidad de cuotas</TitleSpan>
                  <Select
                    value={installments.toString()}
                    onValueChange={v => setInstallments(Number(v))}
                    disabled={isPending}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar cuotas" />
                    </SelectTrigger>
                    <SelectContent>
                      {installmentsOptions.map(opt => (
                        <SelectItem key={opt} value={opt.toString()}>
                          {opt} cuotas
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <TitleSpan>Monto a financiar</TitleSpan>
                  <Input
                    type="number"
                    value={installmentAmount || ''}
                    onChange={e => setInstallmentAmount(Number(e.target.value))}
                    disabled={isPending}
                    min={1}
                    max={remainingBalance - initialAmount}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <AlertWarning>
            {amountPaid > 0 ? (
              <span>
                El valor de cuota se calcula como <strong> <InlinePriceLabel price={installmentAmount} /> / {installments} = <InlinePriceLabel price={installmentValue} />
                </strong>. Los pagos existentes (<strong><InlinePriceLabel price={amountPaid} /></strong>) se vincularán a este nuevo plan de pagos como "entrega inicial".
              </span>
            ) : (
              <span>
                El valor de cuota se calcula como <strong> <InlinePriceLabel price={installmentAmount} /> / {installments} = <InlinePriceLabel price={installmentValue} />
                </strong>{initialAmount > 0 ? <>
                  <br /> Se considerará una entrega inicial de <strong><InlinePriceLabel price={initialAmount} /></strong>.
                </> : null}
              </span>
            )}
          </AlertWarning>
        </CardContent>

        <DialogFooter className="mt-2">
          <div className='flex justify-between w-full'>
            <Button variant="outline" onClick={onClose} disabled={isPending}>
              <X className="h-3.5 w-3.5 mr-1.5" />
              Cancelar
            </Button>
            <Button
              variant="info"
              onClick={handleConfirm}
              disabled={installments <= 0 || installmentAmount <= 0 || isPending}
            >
              <CreditCard className="h-3.5 w-3.5 mr-1" />
              Crear plan de pagos
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
